import { ZPolygon2D } from "../schemas/foundationSchema";
import { ZRoutingGraph } from "../schemas/routingGraph.schema";
export function handlePipeRouting(routing) {
    const validatedRouting = ZRoutingGraph.parse(routing);
    // TODO: Implement actual pipe routing logic.
    return validatedRouting;
}
const DEFAULT_DEMO_Z = 0;
const DEMO_VERTICAL_RISE_M = 0.2; // 200 mm
const DEMO_FIRST_HORIZONTAL_SEGMENT_M = 1;
const DEMO_SECOND_HORIZONTAL_SEGMENT_M = 1;
const DEMO_SAMPLE_STEP = 0.1;
export function createDemoRoomPipeRouting(roomPolygon, args) {
    const { projectNo, device, elevation, floorName, roomNumber } = args;
    const deviceId = String(device._id ?? "").trim();
    if (!deviceId) {
        throw new Error("createDemoRoomPipeRouting requires device._id to link routing to device");
    }
    const polygon = normalizePolygon(ZPolygon2D.parse(roomPolygon));
    const baseX = device.coordinate[0];
    const baseY = device.coordinate[1];
    const baseZ = device.coordinate[2] ?? elevation ?? DEFAULT_DEMO_Z;
    const baseDirection = normalizeDirection2D(device.direction);
    const routingFloorName = floorName?.trim() || device.floor?.trim() || "DEMO";
    const routingRoomNumber = roomNumber ?? device.roomNumber;
    const dir90Candidates = [rotate2D(baseDirection, 90), rotate2D(baseDirection, -90)];
    const directionPairs = [];
    for (const first of dir90Candidates) {
        directionPairs.push({ first, second: rotate2D(first, 45) });
        directionPairs.push({ first, second: rotate2D(first, -45) });
    }
    for (const pair of directionPairs) {
        const p0 = [baseX, baseY, baseZ];
        const p1 = [baseX, baseY, baseZ + DEMO_VERTICAL_RISE_M];
        const p2 = [
            p1[0] + pair.first[0] * DEMO_FIRST_HORIZONTAL_SEGMENT_M,
            p1[1] + pair.first[1] * DEMO_FIRST_HORIZONTAL_SEGMENT_M,
            p1[2],
        ];
        const p3 = [
            p2[0] + pair.second[0] * DEMO_SECOND_HORIZONTAL_SEGMENT_M,
            p2[1] + pair.second[1] * DEMO_SECOND_HORIZONTAL_SEGMENT_M,
            p2[2],
        ];
        const evalResult = evaluateDemoPolylineInsidePolygon([
            [p0[0], p0[1]],
            [p2[0], p2[1]],
            [p3[0], p3[1]],
        ], polygon);
        if (!evalResult.ok) {
            continue;
        }
        return ZRoutingGraph.parse({
            projectNo,
            systemCode: "DEMO_PIPE",
            systemName: "Demo pipe in room",
            floorName: routingFloorName,
            roomNumber: routingRoomNumber,
            nodes: [
                {
                    id: "n1",
                    posM: p0,
                    kind: "DEVICE_PORT",
                    deviceRef: {
                        deviceId,
                        compCodeSnapshot: device.compCode,
                        port: "OUT",
                    },
                },
                { id: "n2", posM: p1, kind: "JUNCTION" },
                { id: "n3", posM: p2, kind: "JUNCTION" },
                { id: "n4", posM: p3, kind: "JUNCTION" },
            ],
            edges: [
                {
                    id: "e1",
                    from: "n1",
                    to: "n2",
                    compCode: "PIPE-DEMO-1",
                    profile: "CIRCULAR",
                    size: { diameterM: 0.16 },
                },
                {
                    id: "e2",
                    from: "n2",
                    to: "n3",
                    compCode: "PIPE-DEMO-2",
                    profile: "CIRCULAR",
                    size: { diameterM: 0.16 },
                },
                {
                    id: "e3",
                    from: "n3",
                    to: "n4",
                    compCode: "PIPE-DEMO-3",
                    profile: "CIRCULAR",
                    size: { diameterM: 0.16 },
                },
            ],
        });
    }
    throw new Error("Could not place demo pipe routing (0.2m up + 90deg 1m + 45deg 1m) inside room polygon.");
}
function normalizePolygon(polygon) {
    if (polygon.length < 4) {
        return [...polygon];
    }
    const first = polygon[0];
    const last = polygon[polygon.length - 1];
    const isClosed = first[0] === last[0] &&
        first[1] === last[1];
    const openPolygon = (isClosed ? polygon.slice(0, -1) : polygon);
    const deduped = [];
    for (const point of openPolygon) {
        const prev = deduped[deduped.length - 1];
        if (prev && prev[0] === point[0] && prev[1] === point[1]) {
            continue;
        }
        deduped.push(point);
    }
    return deduped;
}
function normalizeDirection2D(direction) {
    const x = direction?.[0] ?? 1;
    const y = direction?.[1] ?? 0;
    const length = Math.hypot(x, y);
    if (length <= Number.EPSILON) {
        return [1, 0];
    }
    return [x / length, y / length];
}
function rotate2D(direction, angleDeg) {
    const angle = (angleDeg * Math.PI) / 180;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    return [
        direction[0] * cos - direction[1] * sin,
        direction[0] * sin + direction[1] * cos,
    ];
}
function evaluateDemoPolylineInsidePolygon(polyline, polygon) {
    for (const point of polyline) {
        if (!isPointInPolygon(point, polygon)) {
            return { ok: false, reason: "point_outside", failedPoint: point };
        }
    }
    for (let i = 0; i < polyline.length - 1; i += 1) {
        if (!isSegmentInsidePolygon(polyline[i], polyline[i + 1], polygon)) {
            return {
                ok: false,
                reason: "segment_outside",
                failedSegmentIndex: i,
            };
        }
    }
    return { ok: true };
}
function isSegmentInsidePolygon(start, end, polygon) {
    const dx = end[0] - start[0];
    const dy = end[1] - start[1];
    const length = Math.hypot(dx, dy);
    const steps = Math.max(1, Math.ceil(length / DEMO_SAMPLE_STEP));
    for (let i = 0; i <= steps; i += 1) {
        const t = i / steps;
        const point = [start[0] + dx * t, start[1] + dy * t];
        if (!isPointInPolygon(point, polygon)) {
            return false;
        }
    }
    return true;
}
function isPointInPolygon(point, polygon) {
    const [px, py] = point;
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
        const [xi, yi] = polygon[i];
        const [xj, yj] = polygon[j];
        const intersects = (yi > py) !== (yj > py) &&
            px < ((xj - xi) * (py - yi)) / ((yj - yi) || Number.EPSILON) + xi;
        if (intersects) {
            inside = !inside;
        }
    }
    return inside;
}
